import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Title, usePermissions } from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    LinearProgress,
    Paper,
    Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import DescriptionIcon from '@mui/icons-material/Description';
import SecurityIcon from '@mui/icons-material/Security';
import ArticleIcon from '@mui/icons-material/Article';
import { ButtonField, Tag } from '@wk/components-v3-react16';
import { JumpstartFilterText, JumpstartFilterSelect } from '../components/JumpstartInputs';
import { API_URL } from '../config';
import { useT } from '../i18n/useT';

// ─── Types ────────────────────────────────────────────────────────────────────

type EventType = 'info' | 'warning' | 'error' | 'success' | 'done' | 'step' | 'summary';

interface VerifyEvent {
    id: number;
    type: EventType;
    message?: string;
    step?: number;
    title?: string;
    // summary fields
    original_size?: number;
    final_size?: number;
    file_type?: string;
    bits_encoded?: number;
    verification_passed?: boolean;
    confidence?: number;
}

interface AvailableFile {
    id: string;
    label: string;
    source: string;
    file_type: string;
    file_id?: number;
    filename?: string;
    sku?: string;
    title?: string;
}

interface Summary {
    original_size: number;
    final_size: number;
    file_type: string;
    bits_encoded: number;
    verification_passed: boolean;
    confidence: number;
}

// ─── Icons & colors ───────────────────────────────────────────────────────────

const stepIcon = (step: number) => {
    switch (step) {
        case 1: return <DescriptionIcon fontSize="small" sx={{ color: 'primary.main' }} />;
        case 2: return <ArticleIcon fontSize="small" sx={{ color: 'primary.main' }} />;
        case 3: return <FingerprintIcon fontSize="small" sx={{ color: 'primary.main' }} />;
        case 4: return <VerifiedUserIcon fontSize="small" sx={{ color: 'primary.main' }} />;
        case 5: return <FingerprintIcon fontSize="small" sx={{ color: 'info.main' }} />;
        case 6: return <SecurityIcon fontSize="small" sx={{ color: 'warning.main' }} />;
        default: return <InfoIcon fontSize="small" sx={{ color: 'primary.main' }} />;
    }
};

const eventIcon = (type: EventType) => {
    switch (type) {
        case 'success': return <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />;
        case 'error':   return <ErrorIcon fontSize="small" sx={{ color: 'error.main' }} />;
        case 'warning': return <WarningIcon fontSize="small" sx={{ color: 'warning.main' }} />;
        case 'step':    return null; // rendered separately
        case 'summary': return <VerifiedUserIcon fontSize="small" sx={{ color: 'primary.main' }} />;
        default:        return <InfoIcon fontSize="small" sx={{ color: 'info.main' }} />;
    }
};

const eventColor = (type: EventType): string => {
    switch (type) {
        case 'success': return '#e8f5e9';
        case 'error':   return '#ffebee';
        case 'warning': return '#fff8e1';
        case 'step':    return '#e3f2fd';
        case 'summary': return '#f3e5f5';
        default:        return 'transparent';
    }
};

// ─── Component ────────────────────────────────────────────────────────────────

export const VerifyPage = () => {
    const t = useT();
    const { permissions } = usePermissions();
    const [running, setRunning] = useState(false);
    const [events, setEvents] = useState<VerifyEvent[]>([]);
    const [finished, setFinished] = useState(false);
    const [summary, setSummary] = useState<Summary | null>(null);
    const [currentStep, setCurrentStep] = useState(0);
    const logEndRef = useRef<HTMLDivElement>(null);
    const esRef = useRef<EventSource | null>(null);
    const idCounter = useRef(0);

    // Form state
    const [availableFiles, setAvailableFiles] = useState<AvailableFile[]>([]);
    const [selectedFile, setSelectedFile] = useState('');
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [consumerName, setConsumerName] = useState('Jan');
    const [consumerSurname, setConsumerSurname] = useState('Novák');
    const [consumerEmail, setConsumerEmail] = useState('jan.novak@example.cz');
    const [consumerAddress, setConsumerAddress] = useState('Václavské náměstí 1, 110 00 Praha 1');
    const [consumerPhone, setConsumerPhone] = useState('+420123456789');

    // Load available files on mount
    useEffect(() => {
        const loadFiles = async () => {
            setLoadingFiles(true);
            try {
                const token = localStorage.getItem('token');
                const resp = await fetch(
                    `${API_URL}/verify/available-files?token=${encodeURIComponent(token || '')}`,
                );
                if (resp.ok) {
                    const data = await resp.json();
                    setAvailableFiles(data.files || []);
                    // Auto-select first test file if available
                    const testFile = (data.files || []).find(
                        (f: AvailableFile) => f.source === 'test',
                    );
                    if (testFile) setSelectedFile(testFile.id);
                    else if (data.files?.length) setSelectedFile(data.files[0].id);
                }
            } catch {
                // ignore
            } finally {
                setLoadingFiles(false);
            }
        };
        if (permissions === 'admin') loadFiles();
    }, [permissions]);

    const appendEvent = useCallback((evt: Omit<VerifyEvent, 'id'>) => {
        const newEvt: VerifyEvent = { ...evt, id: ++idCounter.current };
        setEvents(prev => [...prev, newEvt]);
        setTimeout(() => logEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }, []);

    const startVerify = useCallback(() => {
        if (running || !selectedFile) return;

        setEvents([]);
        setSummary(null);
        setFinished(false);
        setCurrentStep(0);
        setRunning(true);

        const token = localStorage.getItem('token');
        if (!token) {
            appendEvent({ type: 'error', message: t('verifyPage.errors.tokenMissing') });
            setRunning(false);
            setFinished(true);
            return;
        }

        const params = new URLSearchParams({
            token,
            file_id: selectedFile,
            consumer_name: consumerName,
            consumer_surname: consumerSurname,
            consumer_email: consumerEmail,
            consumer_address: consumerAddress,
            consumer_phone: consumerPhone,
        });

        const url = `${API_URL}/verify/stream?${params.toString()}`;
        const es = new EventSource(url);
        esRef.current = es;

        es.onmessage = (e) => {
            try {
                const data = JSON.parse(e.data) as VerifyEvent;

                if (data.type === 'done') {
                    es.close();
                    esRef.current = null;
                    setRunning(false);
                    setFinished(true);
                    return;
                }

                if (data.type === 'step') {
                    setCurrentStep(data.step || 0);
                    appendEvent({
                        type: 'step',
                        step: data.step,
                        title: data.title,
                        message: t('verifyPage.log.stepFormat', { step: data.step ?? 0, title: data.title ?? '' }),
                    });
                    return;
                }

                if (data.type === 'summary') {
                    setSummary({
                        original_size: data.original_size || 0,
                        final_size: data.final_size || 0,
                        file_type: data.file_type || '',
                        bits_encoded: data.bits_encoded || 0,
                        verification_passed: data.verification_passed || false,
                        confidence: data.confidence || 0,
                    });
                    return;
                }

                appendEvent(data);
            } catch {
                // ignore parse errors
            }
        };

        es.onerror = () => {
            appendEvent({ type: 'error', message: t('verifyPage.errors.sseInterrupted') });
            es.close();
            esRef.current = null;
            setRunning(false);
            setFinished(true);
        };
    }, [running, selectedFile, consumerName, consumerSurname, consumerEmail, consumerAddress, consumerPhone, appendEvent, t]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            esRef.current?.close();
        };
    }, []);

    if (permissions !== 'admin') {
        return (
            <Box p={3}>
                <Typography variant="h6" color="error">
                    {t('verifyPage.permissionDenied')}
                </Typography>
            </Box>
        );
    }

    const totalSteps = 6;

    return (
        <Box>
            <Title title={t('verifyPage.title')} />

            {/* ── Header ── */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                        <VerifiedUserIcon color="primary" fontSize="large" />
                        <Box>
                            <Typography variant="h5" fontWeight={600}>
                                {t('verifyPage.heading')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('verifyPage.description')}
                            </Typography>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* ── File Selection ── */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} mb={2}>
                        {t('verifyPage.fileSection.title')}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                        <JumpstartFilterSelect
                            label={t('verifyPage.fileSection.selectLabel')}
                            value={selectedFile}
                            onChange={setSelectedFile}
                            fullWidth
                            choices={[
                                { id: '', name: loadingFiles ? t('verifyPage.fileSection.loading') : t('verifyPage.fileSection.placeholder') },
                                ...availableFiles.map((f) => ({ id: String(f.id), name: f.label })),
                            ]}
                        />
                    </Box>
                </CardContent>
            </Card>

            {/* ── Consumer Data ── */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} mb={2}>
                        {t('verifyPage.consumerSection.title')}
                    </Typography>
                    <Box display="flex" gap={2} flexWrap="wrap">
                        <JumpstartFilterText
                            label={t('verifyPage.consumerSection.name')}
                            value={consumerName}
                            onChange={setConsumerName}
                            disabled={running}
                            minWidth={150}
                        />
                        <JumpstartFilterText
                            label={t('verifyPage.consumerSection.surname')}
                            value={consumerSurname}
                            onChange={setConsumerSurname}
                            disabled={running}
                            minWidth={150}
                        />
                        <JumpstartFilterText
                            label={t('verifyPage.consumerSection.email')}
                            value={consumerEmail}
                            onChange={setConsumerEmail}
                            disabled={running}
                            minWidth={250}
                        />
                        <JumpstartFilterText
                            label={t('verifyPage.consumerSection.address')}
                            value={consumerAddress}
                            onChange={setConsumerAddress}
                            disabled={running}
                            minWidth={300}
                        />
                        <JumpstartFilterText
                            label={t('verifyPage.consumerSection.phone')}
                            value={consumerPhone}
                            onChange={setConsumerPhone}
                            disabled={running}
                            minWidth={180}
                        />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <ButtonField type="primary" size="large">
                        <button type="button" onClick={startVerify} disabled={running || !selectedFile}>
                            {running ? t('verifyPage.actions.running') : t('verifyPage.actions.start')}
                        </button>
                    </ButtonField>
                </CardContent>
            </Card>

            {/* ── Progress bar ── */}
            {running && (
                <Card sx={{ mb: 3 }}>
                    <CardContent sx={{ pb: '12px !important' }}>
                        <Box display="flex" alignItems="center" gap={2} mb={1}>
                            <Typography variant="body2" color="text.secondary">
                                {t('verifyPage.progress.stepLabel', { current: currentStep, total: totalSteps })}
                            </Typography>
                            <Box flex={1}>
                                <LinearProgress
                                    variant="determinate"
                                    value={(currentStep / totalSteps) * 100}
                                    sx={{ height: 8, borderRadius: 4 }}
                                />
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* ── Summary chips ── */}
            {summary && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
                            {t('verifyPage.result.title')}
                        </Typography>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <Tag type="dark" color={summary.verification_passed ? 'success' : 'danger'}>
                                {summary.verification_passed ? t('verifyPage.result.verified') : t('verifyPage.result.notVerified')}
                            </Tag>
                            <Tag type="light" color={summary.confidence >= 80 ? 'success' : summary.confidence >= 50 ? 'warning' : 'danger'}>
                                {t('verifyPage.result.confidence', { value: summary.confidence })}
                            </Tag>
                            <Tag type="light" color="info">{t('verifyPage.result.bits', { value: summary.bits_encoded })}</Tag>
                            <Tag type="light" color="strong">{t('verifyPage.result.type', { value: summary.file_type.toUpperCase() })}</Tag>
                            <Tag type="light" color="strong">{t('verifyPage.result.original', { value: (summary.original_size / 1024).toFixed(0) })}</Tag>
                            <Tag type="light" color="strong">{t('verifyPage.result.final', { value: (summary.final_size / 1024).toFixed(0) })}</Tag>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* ── Log area ── */}
            {(events.length > 0 || running) && (
                <Card>
                    <CardContent>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                {t('verifyPage.log.title')}
                            </Typography>
                            {running && <CircularProgress size={18} />}
                            {finished && !running && (
                                <Tag type="light" color={summary?.verification_passed ? 'success' : 'strong'}>
                                    {summary?.verification_passed ? t('verifyPage.log.completedVerified') : t('verifyPage.log.completed')}
                                </Tag>
                            )}
                        </Box>
                        <Paper
                            variant="outlined"
                            sx={{
                                maxHeight: 600,
                                overflowY: 'auto',
                                p: 1,
                                backgroundColor: '#fafafa',
                                fontFamily: 'monospace',
                                fontSize: '0.82rem',
                            }}
                        >
                            {events.map((evt) => (
                                <Box
                                    key={evt.id}
                                    display="flex"
                                    alignItems="flex-start"
                                    gap={0.8}
                                    px={0.5}
                                    py={0.4}
                                    sx={{
                                        backgroundColor: eventColor(evt.type),
                                        borderRadius: 0.5,
                                        mb: 0.2,
                                        ...(evt.type === 'step' ? {
                                            borderLeft: '3px solid',
                                            borderLeftColor: 'primary.main',
                                            fontWeight: 700,
                                            mt: 1,
                                        } : {}),
                                    }}
                                >
                                    <Box mt={0.1} flexShrink={0}>
                                        {evt.type === 'step' ? stepIcon(evt.step || 0) : eventIcon(evt.type)}
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: 'monospace',
                                            fontSize: '0.82rem',
                                            wordBreak: 'break-word',
                                            fontWeight: evt.type === 'step' ? 700 : 400,
                                        }}
                                    >
                                        {evt.message}
                                    </Typography>
                                </Box>
                            ))}
                            <div ref={logEndRef} />
                        </Paper>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export const verifyIcon = VerifiedUserIcon;
